import React, { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import Modal from "../../../components/Modal";
import type { PainelAguardandoBordaItem } from "../../../services/painelService";
import {
  filaDescricaoJsonParaTexto,
  formatPainelDescricaoResumo,
} from "../utils/descricaoPainel";
import { segundosDesdeInicio } from "../utils/temposPainel";
import { formatParado } from "../../SequenciaPedidos/utils/formatTempo";
import { filaTipoLabel } from "../../../services/filaPizzasService";

interface BordasEmFaltaModalProps {
  isOpen: boolean;
  onClose: () => void;
  itens: PainelAguardandoBordaItem[];
  nowMs: number;
}

export const BordasEmFaltaModal: React.FC<BordasEmFaltaModalProps> = ({
  isOpen,
  onClose,
  itens,
  nowMs,
}) => {
  const agrupadoPorBorda = useMemo(() => {
    const map = new Map<string, PainelAguardandoBordaItem[]>();
    for (const item of itens) {
      const key = item.bordaNome.trim() || "borda pendente";
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], "pt-BR"),
    );
  }, [itens]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Bordas em falta (${itens.length})`}
      panelClassName="max-w-lg"
      compactHeader
      bodyClassName="max-h-[70vh] overflow-y-auto p-4"
    >
      {itens.length === 0 ? (
        <p className="text-sm text-neutral-600">
          Nenhuma pizza aguardando borda no momento.
        </p>
      ) : (
        <div className="space-y-4">
          {agrupadoPorBorda.map(([bordaNome, rows]) => (
            <section key={bordaNome}>
              <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
                <AlertTriangle size={16} className="shrink-0" aria-hidden />
                {bordaNome}
                <span className="font-normal text-neutral-500">
                  ({rows.length})
                </span>
              </h4>
              <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200">
                {rows.map((item) => {
                  const descTexto = filaDescricaoJsonParaTexto(item.descricao);
                  const resumo = formatPainelDescricaoResumo(
                    descTexto || null,
                  );
                  const paradoSeg = segundosDesdeInicio(
                    item.paradoDesde,
                    nowMs,
                  );
                  const isSalao =
                    item.tipo === "salao_doce" ||
                    item.tipo === "salao_salgada";
                  return (
                    <li
                      key={`${item.tipo}-${item.id}`}
                      className={
                        isSalao
                          ? "bg-[#8B0000] px-3 py-2.5 text-sm text-white"
                          : "px-3 py-2.5 text-sm"
                      }
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <span
                          className={`font-bold ${
                            isSalao ? "text-white" : "text-neutral-900"
                          }`}
                        >
                          #{item.displayId}
                        </span>
                        <span
                          className={`text-xs font-medium ${
                            isSalao ? "text-amber-200" : "text-amber-800"
                          }`}
                        >
                          parado {formatParado(paradoSeg)}
                        </span>
                      </div>
                      {isSalao && (
                        <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-white/90">
                          Salão
                          {item.customerName ? ` · ${item.customerName}` : ""}
                        </p>
                      )}
                      {!isSalao && item.customerName && (
                        <p className="mt-0.5 text-neutral-600">
                          {item.customerName}
                        </p>
                      )}
                      <p
                        className={`mt-0.5 ${
                          isSalao ? "text-white/80" : "text-neutral-500"
                        }`}
                      >
                        {resumo}
                      </p>
                      <p
                        className={`mt-0.5 text-xs uppercase tracking-wide ${
                          isSalao ? "text-white/60" : "text-neutral-400"
                        }`}
                      >
                        {filaTipoLabel(item.tipo)}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Modal>
  );
};
